import { baseMapOptions, circleSymbol, loadGoogleMaps } from '@lib/googleMaps'
import { darkOrange, gold, orange, white } from '@/theme/colors'
import { fontSizes } from '@/theme/typography'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { frameCamera } from '../core/camera'
import { MapModel } from '../core/map'
import { destinationColor } from '../destinationPalette'
import DayPicker, { DayPickerProps } from './DayPicker'
import {
	MapChip,
	MapLabel,
	MapRoot,
	MapTarget,
	NoPinsCard,
	NoPinsTitle,
	OverlayActions,
	OverlayRow,
	StatusOverlay,
} from './DayMap.styles'

export interface DayMapProps {
	/** The selected day's pins and lines, or the whole trip's. */
	model: MapModel
	/** "Whole trip · 21 days", shown when there is no `dayPicker`. */
	label: string
	/** On a day: the label is the day's chip, which opens the picker of every trip day. */
	dayPicker?: DayPickerProps
	wholeTripAvailable: boolean
	onToggleWholeTrip: () => void
	onOpenFullMap: () => void
	onPinClick: (eventId: number, dayIndex: number) => void
	/** The line under "No events on the map yet" — member mode says how pins arrive; public mode has none. */
	noPinsHint?: string
}

const DAY_PIN_RADIUS = 15
const TRIP_PIN_RADIUS = 6
const FIT_PADDING = { top: 90, bottom: 70, left: 70, right: 70 }
const MAX_FIT_ZOOM = 14
const EMPTY_ZOOM = 12

const circle = (radius: number, fill: string): google.maps.Symbol =>
	circleSymbol(radius, fill, { color: white, weight: 2 })

const dashed = (color: string, weight: number, gap: string): google.maps.PolylineOptions => ({
	strokeOpacity: 0,
	icons: [
		{
			icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.8, strokeColor: color, scale: weight },
			offset: '0',
			repeat: gap,
		},
	],
})

/**
 * The selected day's events on Google Maps — numbered pins in event order, the
 * cached route between consecutive pins (a dashed straight line until one
 * arrives) — or, in whole-trip mode, every event as a small dot in its
 * destination's colour with a dashed line through each day's first pin.
 */
const DayMap: React.FC<DayMapProps> = ({
	model,
	label,
	dayPicker,
	wholeTripAvailable,
	onToggleWholeTrip,
	onOpenFullMap,
	onPinClick,
	noPinsHint,
}) => {
	const containerRef = useRef<HTMLDivElement | null>(null)
	const mapRef = useRef<google.maps.Map | null>(null)
	const overlaysRef = useRef<Array<google.maps.Marker | google.maps.Polyline>>([])
	const refitRef = useRef<() => void>(() => {})
	const fittedRef = useRef<string | null>(null)
	const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
	const [errorMsg, setErrorMsg] = useState<string | null>(null)

	useEffect(() => {
		let cancelled = false
		loadGoogleMaps()
			.then(() => {
				if (cancelled || !containerRef.current) return
				mapRef.current = new google.maps.Map(containerRef.current, {
					...baseMapOptions,
					center: { lat: 0, lng: 0 },
					zoom: 2,
					mapTypeControl: false,
					streetViewControl: false,
					fullscreenControl: false,
					zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
				})
				setStatus('ready')
			})
			.catch(err => {
				if (cancelled) return
				setErrorMsg(err instanceof Error ? err.message : 'Failed to load map')
				setStatus('error')
			})
		return () => {
			cancelled = true
			overlaysRef.current.forEach(o => o.setMap(null))
			overlaysRef.current = []
			mapRef.current = null
		}
	}, [])

	// The pane resizes with the viewport; the map has to be told, then re-fit.
	useEffect(() => {
		const node = containerRef.current
		if (!node || typeof ResizeObserver !== 'function') return
		const observer = new ResizeObserver(() => {
			if (!mapRef.current) return
			google.maps.event.trigger(mapRef.current, 'resize')
			refitRef.current()
		})
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	const sync = useCallback(() => {
		const map = mapRef.current
		if (!map) return
		overlaysRef.current.forEach(o => o.setMap(null))
		overlaysRef.current = []

		const emptyCenter = model.mode === 'day' ? model.emptyCenter : null

		if (model.mode === 'day') {
			model.pins.forEach(pin => {
				const marker = new google.maps.Marker({
					position: { lat: pin.lat, lng: pin.lng },
					map,
					title: pin.title,
					icon: circle(DAY_PIN_RADIUS, orange),
					label: {
						text: String(pin.order),
						color: white,
						fontSize: fontSizes.caption,
						fontWeight: '700',
					},
					zIndex: pin.order,
				})
				marker.addListener('click', () => onPinClick(pin.eventId, pin.dayIndex))
				overlaysRef.current.push(marker)
			})
			const pinById = new Map(model.pins.map(pin => [pin.eventId, pin]))
			model.routes.forEach(route => {
				const from = pinById.get(route.fromEventId)
				const to = pinById.get(route.toEventId)
				if (!from || !to) return
				const decoded = route.encodedPath
					? google.maps.geometry.encoding.decodePath(route.encodedPath)
					: null
				const polyline = decoded
					? new google.maps.Polyline({
							map,
							path: decoded,
							strokeColor: gold,
							strokeWeight: 3,
							strokeOpacity: 0.85,
						})
					: new google.maps.Polyline({
							map,
							path: [
								{ lat: from.lat, lng: from.lng },
								{ lat: to.lat, lng: to.lng },
							],
							...dashed(gold, 3, '14px'),
						})
				overlaysRef.current.push(polyline)
			})
		} else {
			model.pins.forEach(pin => {
				const marker = new google.maps.Marker({
					position: { lat: pin.lat, lng: pin.lng },
					map,
					title: pin.title,
					icon: circle(TRIP_PIN_RADIUS, destinationColor(pin.colorIndex).fill),
				})
				marker.addListener('click', () => onPinClick(pin.eventId, pin.dayIndex))
				overlaysRef.current.push(marker)
			})
			if (model.path.length > 0) {
				overlaysRef.current.push(
					new google.maps.Polyline({ map, path: model.path, ...dashed(darkOrange, 2, '10px') }),
				)
			}
		}

		// The camera jumps rather than animates: between days an animated fit
		// swept through zoom levels whose tiles had not loaded.
		const refit = () => {
			// A map Google has disabled (a key refused for this site) has no element to measure.
			const node = map.getDiv() as HTMLElement | undefined
			if (!node) return
			const framed = frameCamera(
				model.pins,
				{ width: node.clientWidth, height: node.clientHeight },
				FIT_PADDING,
				MAX_FIT_ZOOM,
			)
			if (framed) map.moveCamera(framed)
			else if (emptyCenter) map.moveCamera({ center: emptyCenter, zoom: EMPTY_ZOOM })
		}
		refitRef.current = refit
		// Re-frame only when what is pinned moved: a realtime refetch of the same
		// pins redraws them without throwing away the viewer's pan and zoom.
		const framing = `${model.mode}|${model.pins.map(p => `${p.lat},${p.lng}`).join(';')}|${emptyCenter ? `${emptyCenter.lat},${emptyCenter.lng}` : ''}`
		if (fittedRef.current !== framing) {
			fittedRef.current = framing
			refit()
		}
	}, [model, onPinClick])

	useEffect(() => {
		if (status === 'ready') sync()
	}, [status, sync])

	return (
		<MapRoot>
			<MapTarget ref={containerRef} />
			<OverlayRow>
				{dayPicker ? <DayPicker {...dayPicker} /> : <MapLabel>{label}</MapLabel>}
				<OverlayActions>
					{wholeTripAvailable && (
						<MapChip
							type="button"
							variant={model.mode === 'trip' ? 'primary' : 'secondary'}
							aria-pressed={model.mode === 'trip'}
							onClick={onToggleWholeTrip}
						>
							Whole trip
						</MapChip>
					)}
					<MapChip type="button" variant="secondary" onClick={onOpenFullMap}>
						Open full map
					</MapChip>
				</OverlayActions>
			</OverlayRow>
			{status === 'ready' && model.pins.length === 0 && (
				<NoPinsCard>
					<NoPinsTitle>No events on the map yet</NoPinsTitle>
					{noPinsHint}
				</NoPinsCard>
			)}
			{status === 'loading' && <StatusOverlay>Loading map…</StatusOverlay>}
			{status === 'error' && (
				<StatusOverlay>Map failed to load{errorMsg ? `: ${errorMsg}` : ''}</StatusOverlay>
			)}
		</MapRoot>
	)
}

export default DayMap
